import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useAuth } from '@/hooks/useAuth';
import { useTermsContent } from '@/hooks/useTermsContent';
import { acceptTerms } from '@/api/content.api';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { toast } from '@/lib/toast';

const TITLE = 'Terms & Conditions Updated';
const DESCRIPTION = 'Please review and accept our updated Terms & Conditions to continue.';
const ACCEPT_LABEL = 'Accept';
const ACCEPTING_LABEL = 'Accepting…';
const ACCEPT_SUCCESS_MESSAGE = 'Thanks - your acceptance has been recorded.';
const TERMS_LOAD_ERROR_FALLBACK = 'We could not load the Terms & Conditions. Please try again.';
const ACCEPT_ERROR_FALLBACK = 'We could not record your acceptance. Please try again.';

/**
 * Blocking re-consent gate (MYN-33). Mounted once at the app-shell level
 * (`App.tsx`), session-scoped rather than per-route. Renders nothing unless
 * the session user carries `needsTermsReconsent` (set by the backend once the
 * published Terms version advances past what the user last accepted).
 *
 * While active, blocks the app behind the shared `Dialog` primitive in a
 * non-dismissible configuration: `showCloseButton={false}` removes the X, and
 * a controlled `open` that never turns false plus a no-op `onOpenChange`
 * absorb Radix's own Escape/overlay-click dismiss requests - Dialog already
 * exposes everything needed for this, so no second overlay implementation is
 * required. The only way out is the "Accept" action.
 */
export function TermsReconsentGate() {
  const { user } = useAuth();
  const open = Boolean(user?.needsTermsReconsent);
  const { data, isLoading, isError, error } = useTermsContent(open);
  const queryClient = useQueryClient();

  // Success/error handling lives on the mutation definition (mnr-standards
  // "Write-action feedback"), not on the .mutate() call site. On success,
  // invalidating `['auth', 'me']` refetches the session - once the backend
  // reflects the acceptance, `needsTermsReconsent` flips false and this gate
  // unmounts itself; the toast fires alongside that (not "instead of" -
  // "Write-action feedback" pairs a closing surface with a toast, only ever
  // omitting the toast when the surface stays open, which isn't the case on
  // success here). `isPending` disables the button so a second rapid click
  // (e.g. the same user with two tabs open) cannot fire a duplicate request.
  const acceptMutation = useMutation({
    mutationFn: acceptTerms,
    onSuccess: () => {
      toast.success(ACCEPT_SUCCESS_MESSAGE);
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        // Intentional no-op: `open` is derived from session state, not local
        // state, so this absorbs Radix's Escape/overlay-click close requests
        // and keeps the dialog mounted until "Accept" succeeds.
      }}
      title={TITLE}
      description={DESCRIPTION}
      showCloseButton={false}
      size="xl"
      footer={
        <Button
          type="button"
          variant="primary"
          onClick={() => acceptMutation.mutate()}
          disabled={acceptMutation.isPending}
        >
          {acceptMutation.isPending ? ACCEPTING_LABEL : ACCEPT_LABEL}
        </Button>
      }
    >
      <div className="max-h-[60vh] overflow-y-auto pr-1">
        {isLoading && (
          <div className="flex justify-center py-10">
            <LoadingSpinner label="Loading Terms & Conditions…" />
          </div>
        )}

        {isError && (
          <p role="alert" className="text-sm text-danger">
            {getApiErrorMessage(error, TERMS_LOAD_ERROR_FALLBACK)}
          </p>
        )}

        {data && (
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-subtext">{data.content}</p>
        )}

        {acceptMutation.isError && (
          <p role="alert" className="mt-4 text-sm text-danger">
            {getApiErrorMessage(acceptMutation.error, ACCEPT_ERROR_FALLBACK)}
          </p>
        )}
      </div>
    </Dialog>
  );
}
